import * as React from 'react'
import DataTable, { TableColumn } from 'react-data-table-component'
import { useEffect, useState, useMemo } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Box, Badge, Tooltip, Select, Flex, Text, Switch, Card } from '@radix-ui/themes'

import Credit from '../interfaces/Credit'
import { CreditState } from '../enums/DocStates'
import { useAuth } from '../hooks/useAuth'
import useCreditFetcher from '../hooks/useCreditFetcher'
import { makeSmallImage } from '../utils/makeImage'
import { fechaIsoToYYYY_MM_DD, formatDate, formatWithThousandSep } from '../utils/utils'
import { AppPath } from '../enums/AppPath'
import useFilteredData from '../hooks/useFilteredData'
import { ScreenWrapper } from '../modules/common/components/ScreenWrapper'

import CardOneSearch from './CardOneSearch'

const dateSort = (rowA: Credit, rowB: Credit) => {
  const now = new Date().getTime()
  const aTime = rowA.createdAt ? new Date(rowA.createdAt).getTime() : now
  const bTime = rowB.createdAt ? new Date(rowB.createdAt).getTime() : now

  if (aTime > bTime) {
    return 1
  }

  if (bTime > aTime) {
    return -1
  }

  return 0
}
const amountSort = (rowA: Credit, rowB: Credit) => {
  const a = rowA.amount
  const b = rowB.amount

  return numericSort(a, b)
}

const numericSort = (a: number, b: number) => {
  if (a > b) {
    return 1
  }

  if (b > a) {
    return -1
  }

  return 0
}

const getStatusColor = (status: string | undefined): any => {
  if (!status) return 'gray'
  const s = status.toUpperCase()

  if (s.includes('APROBADO') || s.includes('APROBADA') || s.includes('APPROVED')) return 'green'

  if (s.includes('RECHAZADO') || s.includes('RECHAZADA') || s.includes('CANCELADO') || s.includes('REJECTED'))
    return 'red'

  if (s.includes('EVALUACION') || s.includes('PROCESO') || s.includes('COTIZACION')) return 'sky'

  if (s.includes('SENT') || s.includes('ENVIADA') || s.includes('ENVIADO')) return 'yellow'

  if (s.includes('TAKEN') || s.includes('TOMADA') || s.includes('TOMADO')) {
    return s.includes('NOT') || s.includes('NO') ? 'gray' : 'purple'
  }

  return 'gray'
}

const getStatusLabel = (status: string | undefined): string => {
  if (!status) return '-'
  const s = status.toUpperCase()

  // Internal constants
  if (s === 'STATUS.CREDIT.APPLICATION.SENT') return 'Enviado'

  if (s === 'STATUS.CREDIT.APPLICATION.APPROVED') return 'Aprobado'

  if (s === 'STATUS.CREDIT.APPLICATION.REJECTED') return 'Rechazado'

  if (s === 'STATUS.CREDIT.APPLICATION.TAKEN') return 'Tomado'

  if (s === 'STATUS.CREDIT.APPLICATION.NOT_TAKEN') return 'No tomado'

  // Autofin specific
  if (s.includes('EVALUACION')) return 'En Evaluación'

  if (s.includes('PROCESO')) return 'En Proceso'

  if (s.includes('COTIZACION')) return 'Cotización'

  if (s.includes('APROBAD')) return 'Aprobado'

  if (s.includes('RECHAZAD')) return 'Rechazado'

  if (s.includes('TOMAD')) return 'Tomado'

  return status
}

const customStyles = {
  rows: {
    style: {
      minHeight: '72px', // override the row height
      fontSize: '14px',
    },
  },
  headCells: {
    style: {
      paddingLeft: '16px',
      paddingRight: '16px',
      fontWeight: 'bold',
      fontSize: '14px',
      backgroundColor: '#f9fafb',
    },
  },
  cells: {
    style: {
      paddingLeft: '16px',
      paddingRight: '16px',
    },
  },
}

const statusOptions = [
  { value: '-1', label: 'Todos los estados' },
  { value: '0', label: 'Enviadas' },
  { value: '1', label: 'Aprobadas' },
  { value: '2', label: 'Tomadas' },
  { value: '3', label: 'Rechazadas' },
  { value: '4', label: 'No tomadas' },
]

const Credits: React.FC = () => {
  const [array, setArray] = useState<Credit[]>([])
  const { userData } = useAuth()
  const { fetchCredits, errorCredit, loading } = useCreditFetcher()
  const [searchTerm, setSearchTerm] = useState('')

  // Filters
  const [filterStatus, setFilterStatus] = useState<string>('-1')
  const [sortApprovedFirst, setSortApprovedFirst] = useState<boolean>(false)

  // Helper function to get the correct status value (handles string "undefined")
  const getCreditStatus = (credit: Credit): string => {
    // autofinStatus can be the string "undefined", which is truthy but not a valid status
    if (credit.autofinStatus && credit.autofinStatus !== 'undefined') {
      return credit.autofinStatus
    }

    return credit.status || ''
  }

  useEffect(() => {
    if (!userData) {
      return
    }
    // Fetch ALL credits (-1)
    fetchAsync(userData?.organization?._id ?? '', -1)
  }, [])

  const fetchAsync = async (orgId: string, status: number) => {
    const credits: Credit[] = await fetchCredits(orgId, status)

    if (!credits) {
      return
    }

    setArray(credits)
  }

  const navigate = useNavigate()

  const onRowClickedHandler = (row: Credit): void => {
    const url = `/${AppPath.Loans}/edit/${row._id}`
    navigate(url)
  }

  // Base Search Filtering
  const searchFiltered = useFilteredData({
    array,
    filterFields: [
      (item) => item?.client?.firstname ?? '',
      (item) => item?.client?.lastname ?? '',
      (item) => item?.institution ?? '',
      (item) => item?.vehicle?.brand ?? '',
      (item) => item?.vehicle?.model ?? '',
      (item) => item?.vehicle?.version ?? '',
      (item) => item?.vehicle?.plate ?? '',
    ],
    searchTerm,
    allowFilter: true,
  })

  // Advanced Filtering & Sorting
  const finalData = useMemo(() => {
    let result = [...searchFiltered]

    // 1. Status Filter (Client-side)
    if (filterStatus !== '-1') {
      const targetStatus = parseInt(filterStatus)
      // Map internal status string to enum number logic used in fetcher
      // Ideally we should have a reliable status field on the object.
      // But `fetchManualCredits` maps status based on query.
      // Since we fetched ALL, we need to replicate the status matching logic or check the status string.

      // Let's use the status string matching logic from backend to be safe,
      // or map enum to expected status strings.
      let expectedStatusStrings: string[] = []

      switch (targetStatus) {
        case CreditState.Enviados:
          expectedStatusStrings = [
            'STATUS.CREDIT.APPLICATION.SENT',
            'En Evaluacion de Analista',
            'En Proceso',
            'Cotizacion',
          ]
          break
        case CreditState.Aprobados:
          expectedStatusStrings = ['STATUS.CREDIT.APPLICATION.APPROVED', 'Aprobado', 'Aprobada']
          break
        case CreditState.Tomados:
          expectedStatusStrings = ['STATUS.CREDIT.APPLICATION.TAKEN', 'Tomado', 'Tomada']
          break
        case CreditState.Rechazados:
          expectedStatusStrings = ['STATUS.CREDIT.APPLICATION.REJECTED', 'Rechazado', 'Rechazada']
          break
        case CreditState.NoTomados:
          expectedStatusStrings = ['STATUS.CREDIT.APPLICATION.NOT_TAKEN']
          break
      }

      result = result.filter((item) => {
        const s = getCreditStatus(item)

        return expectedStatusStrings.includes(s) || expectedStatusStrings.some((es) => s.includes(es))
      })
    }

    // 2. Sort Approved First
    if (sortApprovedFirst) {
      result.sort((a, b) => {
        const statusA = getCreditStatus(a).toUpperCase()
        const statusB = getCreditStatus(b).toUpperCase()

        // Prioritize both Approved and Taken (Tomado)
        // Match both English (APPROVED, TAKEN) and Spanish (APROBAD, TOMAD) status strings
        const isPriorityA =
          statusA.includes('APPROVED') ||
          statusA.includes('TAKEN') ||
          statusA.includes('APROBAD') ||
          statusA.includes('TOMAD')
        const isPriorityB =
          statusB.includes('APPROVED') ||
          statusB.includes('TAKEN') ||
          statusB.includes('APROBAD') ||
          statusB.includes('TOMAD')

        if (isPriorityA && !isPriorityB) return -1

        if (!isPriorityA && isPriorityB) return 1

        return 0
      })
    }

    return result
  }, [searchFiltered, filterStatus, sortApprovedFirst])

  const columns: TableColumn<Credit>[] = useMemo(() => {
    // Helper to wrap sort function with "Approved First" logic
    const wrapSort = (sortFn: (a: Credit, b: Credit) => number) => {
      return (a: Credit, b: Credit) => {
        if (!a || !b) return 0

        if (sortApprovedFirst) {
          const statusA = getCreditStatus(a).toUpperCase()
          const statusB = getCreditStatus(b).toUpperCase()

          // Prioritize both Approved and Taken (Tomado)
          // Match both English (APPROVED, TAKEN) and Spanish (APROBAD, TOMAD) status strings
          const isPriorityA =
            statusA.includes('APPROVED') ||
            statusA.includes('TAKEN') ||
            statusA.includes('APROBAD') ||
            statusA.includes('TOMAD')
          const isPriorityB =
            statusB.includes('APPROVED') ||
            statusB.includes('TAKEN') ||
            statusB.includes('APROBAD') ||
            statusB.includes('TOMAD')

          if (isPriorityA && !isPriorityB) return -1

          if (!isPriorityA && isPriorityB) return 1
        }

        return sortFn(a, b)
      }
    }

    // Default string sorter for columns without custom sort
    const stringSort = (selector: (row: Credit) => string) => (a: Credit, b: Credit) => {
      const valA = selector(a) || ''
      const valB = selector(b) || ''

      return valA.localeCompare(valB)
    }

    return [
      {
        name: 'Estado',
        selector: (row) => getCreditStatus(row),
        sortable: true,
        sortFunction: wrapSort(stringSort((row) => getCreditStatus(row))),
        cell: (row) => {
          const status = getCreditStatus(row)

          return (
            <Badge variant="solid" color={getStatusColor(status)}>
              {getStatusLabel(status)}
            </Badge>
          )
        },
        grow: 1,
      },
      {
        name: 'Fuente',
        selector: (row) => row.source ?? 'manual',
        sortable: true,
        sortFunction: wrapSort(stringSort((row) => row.source ?? 'manual')),
        cell: (row) => (
          <Tooltip content={row.source === 'autofin' ? `Evaluación Autofin ID: ${row.autofinId}` : 'Crédito manual'}>
            <Badge variant="outline" color={row.source === 'autofin' ? 'indigo' : 'gray'}>
              {row.source === 'autofin' ? '🤖 Autofin' : '📝 Manual'}
            </Badge>
          </Tooltip>
        ),
        grow: 0,
      },
      {
        name: 'Institución',
        selector: (row) => row.institution,
        sortable: true,
        sortFunction: wrapSort(stringSort((row) => row.institution)),
        grow: 1,
      },
      {
        name: 'Monto',
        selector: (row) => formatWithThousandSep(row.amount),
        sortable: true,
        right: true,
        sortFunction: wrapSort(amountSort),
        grow: 0,
      },
      {
        name: 'Cliente',
        selector: (row) => `${row.client?.firstname ?? ''} ${row.client?.lastname ?? ''}`,
        grow: 2,
        sortable: true,
        sortFunction: wrapSort(stringSort((row) => `${row.client?.firstname ?? ''} ${row.client?.lastname ?? ''}`)),
      },
      {
        name: 'Negocio',
        selector: (row) => {
          const dealId = typeof row.deal === 'object' && row.deal !== null ? (row.deal as any)._id : row.deal

          return dealId || ''
        },
        sortable: false,
        cell: (row) => {
          const dealId = typeof row.deal === 'object' && row.deal !== null ? (row.deal as any)._id : row.deal

          return dealId ? (
            <Link
              to={`/${AppPath.Deals}/view/${dealId}`}
              state={{ from: 'credits' }}
              onClick={(e) => e.stopPropagation()}
              className="text-blue-600 hover:text-blue-800 hover:underline"
            >
              Ver Negocio
            </Link>
          ) : (
            '-'
          )
        },
        grow: 1,
      },
      {
        name: 'Miniatura',
        grow: 0,
        cell: (row) => (
          <img
            style={{ borderRadius: '4px', objectFit: 'cover' }}
            width="64px"
            height="48px"
            alt={row.vehicle?.brand ?? ''}
            src={makeSmallImage(row.vehicle?.pictures?.public?.[0] ?? '')}
          />
        ),
      },
      {
        name: 'Vehículo',
        selector: (row) =>
          row.vehicle
            ? `${row.vehicle.brand} ${row.vehicle.model} ${row.vehicle.version ?? ''} (${row.vehicle.year})`
            : 'Sin vehículo asignado',
        grow: 2,
        sortable: true,
        sortFunction: wrapSort(
          stringSort((row) =>
            row.vehicle
              ? `${row.vehicle.brand} ${row.vehicle.model} ${row.vehicle.version ?? ''} (${row.vehicle.year})`
              : 'Sin vehículo asignado'
          )
        ),
      },
      {
        name: 'Patente',
        selector: (row) => row.vehicle?.plate ?? '-',
        sortable: true,
        sortFunction: wrapSort(stringSort((row) => row.vehicle?.plate ?? '-')),
        grow: 0,
      },

      {
        name: 'Fecha solicitud',
        selector: (row) => formatDate(fechaIsoToYYYY_MM_DD(row.createdAt)),
        sortable: true,
        sortFunction: wrapSort(dateSort),
        right: true,
      },
    ]
  }, [sortApprovedFirst])

  const onClearClick = () => {
    setFilterStatus('-1')
    setSortApprovedFirst(false)
  }

  return (
    <ScreenWrapper>
      <CardOneSearch
        headerText={'Gestionar '}
        docuType={AppPath.Loans}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        onClearClick={onClearClick}
      />

      <div className="mt-2 p-2 mx-auto ">
        <Card className="mb-4 bg-gray-50">
          <Flex gap="6" align="center" wrap="wrap">
            <Flex gap="2" align="center">
              <Text size="2" weight="bold">
                Estado:
              </Text>
              <Select.Root value={filterStatus} onValueChange={setFilterStatus}>
                <Select.Trigger placeholder="Filtrar por estado" />
                <Select.Content>
                  {statusOptions.map((opt) => (
                    <Select.Item key={opt.value} value={opt.value}>
                      {opt.label}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Flex>

            <Flex gap="2" align="center">
              <Text size="2">Aprobados primero</Text>
              <Switch checked={sortApprovedFirst} onCheckedChange={setSortApprovedFirst} />
            </Flex>
          </Flex>
        </Card>

        <Box mt="4">
          <DataTable
            key={sortApprovedFirst ? 'approved-first' : 'standard'}
            columns={columns}
            data={finalData}
            highlightOnHover
            pointerOnHover
            onRowClicked={onRowClickedHandler}
            pagination
            paginationPerPage={20}
            paginationRowsPerPageOptions={[20, 50, 100]}
            customStyles={customStyles}
            noDataComponent={
              loading ? (
                <Text className="p-4">Cargando...</Text>
              ) : (
                <Text className="p-4">No se encontraron solicitudes.</Text>
              )
            }
            // CRITICAL: Disable default sorting to preserve finalData order
            defaultSortFieldId={null}
            defaultSortAsc={false}
            sortFunction={null}
          />
        </Box>
        <div>{errorCredit?.message}</div>
      </div>
    </ScreenWrapper>
  )
}
export default Credits
